import { JSX, SVGProps } from 'react';
import { aboveBarLabelTextStyles } from './aboveBarLabel.styles';

/** Gap in px between the top of the bar and the baseline of its label. */
const LABEL_GAP = 6;

const LABEL_FONT_SIZE = 11;

/**
 * The chart library hands a bar label its geometry plus its whole owner state, and the
 * formatted text as `children`. Only the geometry and the fade flag are used here; the
 * rest exists to be stripped, because forwarding `seriesId` or `isHighlighted` onto an SVG
 * `<text>` makes React log an unknown-attribute warning -- once per label, which is around
 * a hundred per render on a twelve-month chart.
 */
interface AboveBarLabelProps extends Omit<SVGProps<SVGTextElement>, 'x' | 'y' | 'width' | 'height' | 'color' | 'ref'> {
    /** The bar's top-left corner, in SVG user space. */
    x: number;
    y: number;
    width: number;
    height: number;
    /** Where the bar's stack starts. Unused -- destructured so it is not forwarded. */
    xOrigin?: number;
    yOrigin?: number;
    seriesId?: string | number;
    dataIndex?: number;
    color?: string;
    isFaded?: boolean;
    isHighlighted?: boolean;
    skipAnimation?: boolean;
    layout?: 'vertical' | 'horizontal';
    classes?: Record<string, string>;
    /**
     * How large to draw the text. Threaded in rather than read from a media query here,
     * because this component runs once per bar and must stay cheap.
     *
     * Deliberately not called `fontSize`: that is an SVG presentation attribute the base
     * props already declare as `string | number`, and shadowing it makes this component
     * unassignable to the slot it is written for.
     */
    labelFontSize?: number;
}

/**
 * A bar's value, printed above the bar rather than inside it.
 *
 * The library's own label is centred in the middle of the bar and cannot be moved: its
 * position is recomputed by an animation hook that overrides anything passed in. Replacing
 * the whole component is the supported way to place it elsewhere, and the chart must still
 * set its `barLabel` prop or this is never rendered at all.
 *
 * Two things the caller has to get right for this to be visible: the y-axis needs headroom
 * above the tallest bar, because the plot is clipped to the drawing area and a label drawn
 * above a full-height bar falls outside it; and the label text must come from a function
 * rather than the built-in shortcut, which suppresses zeros.
 */
export const AboveBarLabel = ({
    // Geometry we consume rather than forward.
    x,
    y,
    width,
    height,
    xOrigin,
    yOrigin,
    // Owner state: none of these is a DOM attribute.
    seriesId,
    dataIndex,
    color,
    isFaded,
    isHighlighted,
    skipAnimation,
    layout,
    classes,
    labelFontSize = LABEL_FONT_SIZE,
    children,
    ...textProps
}: AboveBarLabelProps): JSX.Element => (
    <text
        {...textProps}
        x={x + width / 2}
        y={y - LABEL_GAP}
        textAnchor="middle"
        // 'auto' puts the glyphs above the given baseline; the built-in uses 'central',
        // which is exactly what centres it inside the bar.
        dominantBaseline="auto"
        opacity={isFaded ? 0.3 : 1}
        style={aboveBarLabelTextStyles(labelFontSize)}
    >
        {children}
    </text>
);
